/**
 * CustomerERP — Sistem Ayarları > Firma Bilgileri (Faz 0).
 * Firma adı, ticari unvan, logo, iletişim, vergi bilgileri, varsayılan para
 * birimi ve KDV. Kaynak: GET/PUT /api/company-profile.
 * Logo dosya depolamaya yüklenmez; küçük görsel olarak (≤512 KB) firma
 * kaydında saklanır.
 */
import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useBranding, BrandMark } from "@/lib/branding";
import { Card, Input, Select, Textarea, Btn } from "@/components/UI";
import { Building2, ImagePlus, Trash2, CheckCircle2 } from "lucide-react";

const EMPTY = {
  company_name: "", trade_name: "", logo_data_url: "", label_logo_data_url: "", address: "", phone: "",
  email: "", tax_office: "", tax_no: "", website: "", default_currency: "TRY",
  default_vat_rate: 20,
};
const LOGO_MAX = 512 * 1024;

export default function CompanyProfileSettings({ canEdit }) {
  const { refresh } = useBranding();
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const fileRef = useRef(null);
  const labelRef = useRef(null);

  useEffect(() => {
    api.get("/company-profile")
      .then((d) => setF({ ...EMPTY, ...d }))
      .catch(() => setMsg({ type: "err", text: "Firma bilgileri yüklenemedi." }))
      .finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const onLogo = (field = "logo_data_url") => (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      setMsg({ type: "err", text: "Logo yalnız PNG, JPG veya WEBP olabilir." });
      return;
    }
    if (file.size > LOGO_MAX) {
      setMsg({ type: "err", text: "Logo en fazla 512 KB olabilir." });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setF((p) => ({ ...p, [field]: String(reader.result || "") }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true); setMsg({ type: "", text: "" });
    try {
      const payload = {};
      Object.keys(EMPTY).forEach((k) => { payload[k] = f[k] ?? EMPTY[k]; });
      payload.default_vat_rate = parseFloat(payload.default_vat_rate || 0);
      const d = await api.put("/company-profile", payload);
      setF({ ...EMPTY, ...d });
      await refresh();
      setMsg({ type: "ok", text: "Firma bilgileri kaydedildi." });
    } catch (e) {
      setMsg({ type: "err", text: e?.response?.data?.detail || "Kaydedilemedi." });
    } finally { setSaving(false); }
  };

  if (loading) return <Card className="p-6 text-sm text-slate-500">Yükleniyor…</Card>;

  return (
    <Card className="p-5" data-testid="company-profile-settings">
      <div className="flex items-center gap-2 mb-4">
        <Building2 size={18} className="text-indigo-600" />
        <h3 className="text-base font-semibold text-slate-800">Firma Bilgileri</h3>
        <span className="text-xs text-slate-500">Ekranlarda, belgelerde ve e-postalarda kullanılır</span>
      </div>

      <div className="flex items-center gap-4 mb-5 p-3 rounded-lg border border-slate-200 bg-slate-50/60">
        {f.logo_data_url ? (
          <img src={f.logo_data_url} alt="Logo" data-testid="company-logo-preview"
            className="h-16 w-16 object-contain rounded bg-white border border-slate-200" />
        ) : (
          <BrandMark className="h-16 w-16" testId="company-logo-placeholder" />
        )}
        <div className="flex-1 text-xs text-slate-500">
          Kurumsal Logo (lacivert/renkli) — proforma, irsaliye, katalog ve diğer belgeler (PNG / JPG / WEBP, en fazla 512 KB)
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              onChange={onLogo()} data-testid="company-logo-input" />
            <Btn variant="secondary" onClick={() => fileRef.current?.click()} data-testid="company-logo-upload-btn">
              <ImagePlus size={14} /> Logo Seç
            </Btn>
            {f.logo_data_url && (
              <Btn variant="secondary" onClick={() => setF({ ...f, logo_data_url: "" })} data-testid="company-logo-remove-btn">
                <Trash2 size={14} /> Kaldır
              </Btn>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-4 mb-5 p-3 rounded-lg border border-slate-200 bg-slate-50/60" data-testid="company-label-logo-box">
        {f.label_logo_data_url
          ? <img src={f.label_logo_data_url} alt="Etiket logosu" data-testid="company-label-logo-preview" className="h-16 w-16 object-contain rounded bg-white border border-slate-200" />
          : <div className="h-16 w-16 rounded border border-dashed border-slate-300 bg-white" />}
        <div className="flex-1 text-xs text-slate-500">Kargo Etiketi Logosu (siyah) — yalnız 10×10 cm kargo / sevkiyat etiketinde kullanılır</div>
        {canEdit && (
          <div className="flex gap-2">
            <input ref={labelRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onLogo("label_logo_data_url")} data-testid="company-label-logo-input" />
            <Btn variant="secondary" onClick={() => labelRef.current?.click()} data-testid="company-label-logo-upload-btn"><ImagePlus size={14} /> Logo Seç</Btn>
            {f.label_logo_data_url && <Btn variant="secondary" onClick={() => setF({ ...f, label_logo_data_url: "" })} data-testid="company-label-logo-remove-btn"><Trash2 size={14} /> Kaldır</Btn>}
          </div>
        )}
      </div>

      <fieldset disabled={!canEdit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input label="Firma Adı" value={f.company_name} onChange={set("company_name")} placeholder="ör. Acme"
          data-testid="company-name-input" />
        <Input label="Ticari Unvan" value={f.trade_name} onChange={set("trade_name")} placeholder="ör. Acme Ticaret A.Ş."
          data-testid="company-trade-name-input" />
        <div className="md:col-span-2">
          <Textarea label="Adres" rows={2} value={f.address} onChange={set("address")} data-testid="company-address-input" />
        </div>
        <Input label="Telefon" value={f.phone} onChange={set("phone")} data-testid="company-phone-input" />
        <Input label="E-posta" type="email" value={f.email} onChange={set("email")} data-testid="company-email-input" />
        <Input label="Vergi Dairesi" value={f.tax_office} onChange={set("tax_office")} data-testid="company-tax-office-input" />
        <Input label="Vergi No" value={f.tax_no} onChange={set("tax_no")} placeholder="10 veya 11 hane"
          data-testid="company-tax-no-input" />
        <Input label="Web Sitesi" value={f.website} onChange={set("website")} placeholder="https://"
          data-testid="company-website-input" />
        <div className="grid grid-cols-2 gap-3">
          <Select label="Varsayılan Para Birimi" value={f.default_currency} onChange={set("default_currency")}
            data-testid="company-currency-select">
            <option value="TRY">TRY</option>
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
            <option value="GBP">GBP</option>
          </Select>
          <Input label="Varsayılan KDV (%)" type="number" min="0" max="100" step="1" value={f.default_vat_rate}
            onChange={set("default_vat_rate")} data-testid="company-vat-input" />
        </div>
      </fieldset>

      {msg.text && (
        <div data-testid="company-profile-msg"
          className={`mt-4 text-sm rounded px-3 py-2 flex items-center gap-2 ${msg.type === "ok" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-rose-50 text-rose-700 border border-rose-200"}`}>
          {msg.type === "ok" && <CheckCircle2 size={14} />} {msg.text}
        </div>
      )}
      {canEdit ? (
        <div className="mt-4"><Btn onClick={save} disabled={saving} data-testid="company-profile-save-btn">{saving ? "Kaydediliyor…" : "Kaydet"}</Btn></div>
      ) : (
        <div className="mt-4 text-xs text-slate-500">Bu bilgileri yalnız yetkili kullanıcılar değiştirebilir.</div>
      )}
    </Card>
  );
}
